import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useNavigate } from 'react-router-dom';
import bannerImg from '../../images/background_img_1.png';
import { SubscriptionPlan } from '../../types/subscriptionPlan';
import {
  INDEPENDENT_BARBER_PLANS,
  SHOP_OWNER_PLANS,
} from '../../defaults/SubscriptionPlans';
import {
  createShopSubscription,
  createBarberSubscription,
  createStripeSubscription,
} from '../../middleware/registration_service/createRequest';
import { StripeSubscription } from '../../types/stripePayments';

const SubscriptionSelection = () => {
  const navigate = useNavigate();
  const businessType = localStorage.getItem('businessType') || '';
  const isBarber = businessType === 'Barber';
  const isShopOwner = businessType === 'Shop Owner';

  const plans = isBarber
    ? INDEPENDENT_BARBER_PLANS
    : isShopOwner
    ? SHOP_OWNER_PLANS
    : [...INDEPENDENT_BARBER_PLANS, ...SHOP_OWNER_PLANS];

  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const selectedPlan = plans.find((p) => p.id === selectedPlanId) || null;

  const handleSelect = (plan: SubscriptionPlan) => {
    setSelectedPlanId(plan.id);
    setError(null);
  };

  const handleConfirm = async () => {
    if (!selectedPlan) {
      setError('Please select a subscription plan to continue.');
      return;
    }

    const expiryDate = new Date();
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    const expiryDateStr = expiryDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD

    setLoading(true);
    setError(null);

    try {
      // Create Stripe subscription first if customer ID and price ID are available
      let stripeSubscriptionId: string | undefined;
      let productId: string | undefined;
      let status:
        | 'incomplete'
        | 'incomplete_expired'
        | 'trialing'
        | 'active'
        | 'past_due'
        | 'canceled'
        | 'unpaid'
        | 'paused'
        | undefined = 'incomplete';

      const regCustomerId =
        localStorage.getItem('reg_customer_id') || undefined;
      if (regCustomerId && selectedPlan.priceId) {
        try {
          const response = await createStripeSubscription({
            customerId: regCustomerId,
            item: { priceId: selectedPlan.priceId },
            trialPeriodDays: selectedPlan.trialDays,
          });
          const stripeSubscriptionResponse: StripeSubscription = response.data;
          console.log(
            'Stripe subscription response:',
            stripeSubscriptionResponse
          );

          stripeSubscriptionId = stripeSubscriptionResponse?.id;
          productId =
            stripeSubscriptionResponse?.items?.data?.[0]?.plan?.product;
          status = stripeSubscriptionResponse?.status;
        } catch (stripeSubErr) {
          console.warn('Could not create Stripe subscription:', stripeSubErr);
        }
      }

      if (selectedPlan.type === 'SHOP_OWNER') {
        const shopIdItem = localStorage.getItem('reg_shop_id');
        if (!shopIdItem) {
          setError('Shop ID not found. Please restart registration.');
          return;
        }
        // TypeScript narrows shopIdItem → string
        const shopId = BigInt(shopIdItem);
        await createShopSubscription({
          name: selectedPlan.name,
          price: selectedPlan.price,
          barberSeatCount: selectedPlan.barberSeatCount ?? 2,
          marketingCampaignCount: selectedPlan.marketingCampaignCount ?? 1,
          emailNotificationCount: selectedPlan.emailNotificationCount ?? 10,
          smsNotificationCount: selectedPlan.smsNotificationCount ?? 10,
          shopId: shopId,
          customerId: regCustomerId,
          active: true,
          expiryDate: expiryDateStr,
          ...(stripeSubscriptionId && { stripeSubscriptionId }),
          ...(productId && { productId }),
          ...(status && { status }),
        });
      } else {
        const barberIdItem = localStorage.getItem('reg_partner_id');
        if (!barberIdItem) {
          setError('Barber ID not found. Please restart registration.');
          return;
        }
        // TypeScript narrows barberIdItem → string
        const barberId = BigInt(barberIdItem);
        await createBarberSubscription({
          name: selectedPlan.name,
          price: selectedPlan.price,
          marketingCampaignCount: selectedPlan.marketingCampaignCount ?? 5,
          emailNotificationCount: selectedPlan.emailNotificationCount ?? 100,
          smsNotificationCount: selectedPlan.smsNotificationCount ?? 100,
          active: true,
          barberId: barberId,
          customerId: regCustomerId,
          expiryDate: expiryDateStr,
          ...(stripeSubscriptionId && { stripeSubscriptionId }),
          ...(productId && { productId }),
          ...(status && { status }),
        });
      }

      // Clean up temporary registration keys
      [
        'reg_shop_owner_id',
        'reg_auth0_user_id',
        'reg_shop_id',
        'reg_owner_email',
        'reg_owner_firstName',
        'reg_owner_lastName',
        'reg_owner_title',
        'reg_owner_phone',
        'reg_partner_id',
        'reg_customer_id',
      ].forEach((k) => localStorage.removeItem(k));

      navigate('/user-login', {
        state: 'Registration complete! Please log in to access your dashboard.',
      });
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to create subscription. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const headingText = isBarber
    ? 'Choose Your Barber Plan'
    : isShopOwner
    ? 'Choose Your Shop Plan'
    : 'Choose Your Subscription Plan';

  return (
    <Box>
      {/* Hero Banner */}
      <Box
        sx={{
          mt: '85px',
          backgroundImage: `linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.65)), url(${bannerImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          py: 8,
          px: 2,
          textAlign: 'center',
          color: '#fff',
        }}
      >
        <Typography variant="h3" fontWeight="bold" gutterBottom>
          {headingText}
        </Typography>
        <Typography
          variant="h6"
          sx={{ maxWidth: 640, mx: 'auto', opacity: 0.85 }}
        >
          Select the plan that best fits your business. You can upgrade or
          change your plan at any time.
        </Typography>

        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            mt: 3,
            px: 3,
            py: 1,
            bgcolor: 'rgba(255,255,255,0.15)',
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.3)',
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 20 }} />
          <Typography variant="body2">
            All plans include a 30-day free trial — no credit card required
          </Typography>
        </Box>
      </Box>

      {/* Plans */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Independent Barber section */}
        {(isBarber || (!isBarber && !isShopOwner)) && (
          <Box sx={{ mb: isShopOwner ? 0 : 5 }}>
            {!isBarber && !isShopOwner && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
              >
                <ContentCutIcon sx={{ color: '#2b2b33' }} />
                <Typography variant="h5" fontWeight="bold">
                  Independent Barber
                </Typography>
              </Box>
            )}
            <Grid
              container
              spacing={3}
              justifyContent={isBarber ? 'center' : 'flex-start'}
            >
              {INDEPENDENT_BARBER_PLANS.map((plan) => (
                <Grid item xs={12} sm={6} md={isBarber ? 5 : 4} key={plan.id}>
                  <PlanCard
                    plan={plan}
                    selected={selectedPlanId === plan.id}
                    onSelect={() => handleSelect(plan)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {!isBarber && !isShopOwner && <Divider sx={{ my: 4 }} />}

        {/* Shop Owner section */}
        {(isShopOwner || (!isBarber && !isShopOwner)) && (
          <Box>
            {!isBarber && !isShopOwner && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
              >
                <StorefrontIcon sx={{ color: '#2b2b33' }} />
                <Typography variant="h5" fontWeight="bold">
                  Shop Owner with Barbers
                </Typography>
              </Box>
            )}
            <Grid
              container
              spacing={3}
              justifyContent={isShopOwner ? 'center' : 'flex-start'}
            >
              {SHOP_OWNER_PLANS.map((plan) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={isShopOwner ? 4 : 4}
                  key={plan.id}
                >
                  <PlanCard
                    plan={plan}
                    selected={selectedPlanId === plan.id}
                    onSelect={() => handleSelect(plan)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Selected plan summary */}
        {selectedPlan && (
          <Box
            sx={{
              mt: 5,
              p: 3,
              bgcolor: '#f0f4ff',
              borderRadius: 2,
              border: '2px solid #1976d2',
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              Selected Plan: {selectedPlan.name} — {selectedPlan.subtitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {selectedPlan.trialDays > 0
                ? `Free for ${
                    selectedPlan.trialDays
                  } days, then £${selectedPlan.price.toFixed(2)}/month`
                : `£${selectedPlan.price.toFixed(2)}/month`}
              {selectedPlan.barberSeatCount
                ? ` · ${selectedPlan.barberSeatCount} barber seats`
                : ''}
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            disabled={!selectedPlan || loading}
            onClick={handleConfirm}
            sx={{
              px: 6,
              py: 1.5,
              bgcolor: '#2b2b33',
              color: '#fff',
              fontWeight: 'bold',
              borderRadius: 2,
              '&:hover': { bgcolor: '#444' },
              '&:disabled': { bgcolor: '#bbb', color: '#fff' },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Confirm Plan & Complete Registration'
            )}
          </Button>
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', mt: 2 }}
        >
          You can change your plan at any time from your account settings.
        </Typography>
      </Container>
    </Box>
  );
};

interface PlanCardProps {
  plan: SubscriptionPlan;
  selected: boolean;
  onSelect: () => void;
}

const PlanCard = ({ plan, selected, onSelect }: PlanCardProps) => (
  <Card
    onClick={onSelect}
    elevation={selected ? 8 : 3}
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 3,
      cursor: 'pointer',
      border: selected ? '2px solid #1976d2' : '2px solid transparent',
      transition: 'transform 0.2s, box-shadow 0.2s, border 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 6,
      },
      position: 'relative',
    }}
  >
    {plan.tag && (
      <Box
        sx={{
          position: 'absolute',
          top: 14,
          right: 14,
        }}
      >
        <Chip
          label={plan.tag}
          size="small"
          sx={{
            bgcolor: plan.tagColor || '#1976d2',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: '0.7rem',
          }}
        />
      </Box>
    )}

    <CardContent sx={{ flexGrow: 1, p: 3 }}>
      {/* Icon */}
      <Box sx={{ mb: 2 }}>
        {plan.type === 'BARBER' ? (
          <ContentCutIcon sx={{ fontSize: 36, color: '#2b2b33' }} />
        ) : (
          <StorefrontIcon sx={{ fontSize: 36, color: '#2b2b33' }} />
        )}
      </Box>

      {/* Name + subtitle */}
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {plan.subtitle}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {plan.name}
      </Typography>

      {/* Price */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
        {plan.price === 0 ? (
          <Typography variant="h4" fontWeight="bold" color="success.main">
            Free
          </Typography>
        ) : (
          <>
            <Typography variant="h4" fontWeight="bold">
              £{plan.price.toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
              /month
            </Typography>
          </>
        )}
      </Box>

      {plan.trialDays > 0 && plan.price > 0 && (
        <Typography
          variant="caption"
          color="success.main"
          sx={{ display: 'block', mb: 1 }}
        >
          {plan.trialDays}-day free trial included
        </Typography>
      )}

      {/* Seat count badge */}
      {plan.barberSeatCount && (
        <Chip
          label={`${plan.barberSeatCount} barber seats`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ mb: 2 }}
        />
      )}

      <Divider sx={{ my: 2 }} />

      {/* Features */}
      {plan.features.map((feat) => (
        <Box
          key={feat}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
        >
          <CheckCircleRoundedIcon
            sx={{ fontSize: 18, color: selected ? '#1976d2' : '#4caf50' }}
          />
          <Typography variant="body2">{feat}</Typography>
        </Box>
      ))}
    </CardContent>

    {/* Select button */}
    <Box sx={{ p: 2, pt: 0 }}>
      <Button
        fullWidth
        variant={selected ? 'contained' : 'outlined'}
        sx={
          selected
            ? {
                bgcolor: '#2b2b33',
                color: '#fff',
                fontWeight: 'bold',
                '&:hover': { bgcolor: '#444' },
              }
            : { fontWeight: 'bold', borderColor: '#2b2b33', color: '#2b2b33' }
        }
      >
        {selected ? '✓ Selected' : 'Select Plan'}
      </Button>
    </Box>
  </Card>
);

export default SubscriptionSelection;
